<template>
  <div>
    <v-row>
      <v-col cols="12">
        <text-field-like
            v-model="processImplementation.name"
            label="プロセス名"
            hide-details>
        </text-field-like>
      </v-col>
      <v-col cols="12">
        <v-select
            v-model="selectedServiceImplementationId"
            :items="serviceImplementationOptions"
            :loading="serviceImplementationsLoading"
            label="サービス実装">
          <template #item="{item}">
            <v-row>
              <v-col cols="6">
                <text-field-like
                    label="サービス構成モデル名"
                    :value="item.localServiceComponentModel.name"
                    hide-details>
                </text-field-like>
              </v-col>
              <v-col cols="6">
                <local-id-field
                    label="内部ID"
                    :value="item.localId"
                    hide-details>
                </local-id-field>
              </v-col>
            </v-row>
          </template>
          <template #selection="{item}">
            <div style="display: inline-flex; justify-content: space-between; width: 100%;">
              <div>{{ item.localServiceComponentModel.name }}</div>
              <div style="font-size: 80%;">内部ID: {{ item.localId }}</div>
            </div>
          </template>
        </v-select>
      </v-col>
    </v-row>

    <el-table
        :data="eventImplementationsView"
        :height="`calc((${mainBounds.height} - 200px) * 0.5)`"
        :class="{'hide-empty-text': eventImplementationsLoading}"
        row-key="id">
      <el-table-column
          prop="isSelected"
          width="60"
          align="center">
        <template #default="{row}">
          <el-tooltip
              v-if="row.isExisting"
              content="作成済み">
            <v-icon color="gray" style="font-size: 17px; color: rgba(0,0,0,0.4);">mdi-checkbox-marked</v-icon>
          </el-tooltip>
          <el-checkbox
              v-else
              :value="row.isSelected"
              @change="handleSelectionChange(row, $event)">
          </el-checkbox>
        </template>
      </el-table-column>

      <el-table-column
          label="プロセス実装"
          prop="processName">
      </el-table-column>

      <el-table-column
          label="イベント実装"
          prop="localId">
        <template #default="{row}">
          <table-inline>
            <text-field-like
                :value="row.name"
                :value-local-id="row.localId"
                placeholder="名称未設定">
            </text-field-like>
          </table-inline>
        </template>
      </el-table-column>

      <el-table-column
          label="説明"
          prop="description">
        <template #default="{row}">
          <table-inline>
            <text-field-like
                :value="row.description"
                placeholder="未設定"
                multi-line>
            </text-field-like>
          </table-inline>
        </template>
      </el-table-column>

      <template #append>
        <v-row
            justify="center"
            align="center"
            style="position: absolute; top: 0; right: 0; bottom: 0; left: 0;"
            v-if="eventImplementationsLoading">
          <v-progress-circular size="64" indeterminate/>
        </v-row>
      </template>
    </el-table>
  </div>
</template>

<script>
import {mapState, mapGetters, mapActions} from "vuex";
import Formatter from "@/utils/Formatter";
import TableInline from "@/components/atoms/TableInline.vue";
import LocalIdField from "@/components/molecules/LocalIdField.vue";
import TextFieldLike from "@/components/molecules/TextFieldLike.vue";
import BetterSet from "betterset";
import gql from "graphql-tag";

export default {
  name: 'EventImplementationSelectForm',
  components: {TextFieldLike, LocalIdField, TableInline},
  mixins: [
    Formatter
  ],
  props: {
    processImplementation: {
      type: Object,
      required: true,
    },
    controllerId: {
      type: String,
      required: true,
    },
    initialServiceImplementationId: {
      type: String,
      default: null
    },
    existingEventImplementationIds: {
      type: Array,
      default: () => ([]),
    },
    value: {
      type: Array,
      default: () => ([]),
    },
    readonly: {
      type: Boolean,
      default: false
    },
  },
  inject: {
    dialog: {default: null},
    form: {default: null},
    mainBounds: { default: () => ({}) },
    getProcessImplementations: { default: () => () => ([]) },
  },
  data() {
    return {
      selectedServiceImplementationId: this.initialServiceImplementationId,
      serviceImplementations: [],
      serviceImplementationsLoading: false,
      eventImplementations: [],
      eventImplementationsLoading: false,
    }
  },
  created() {
    this.form && this.form.register(this);
  },
  beforeDestroy() {
    this.form && this.form.unregister(this)
  },
  mounted() {
    this.loadServiceImplementations();
  },
  computed: {
    ...mapGetters({
      hcsClient: 'hcs/client',
      hdsEntities: 'hds/entities',
      account: 'hcs/account'
    }),
    processImplementations() {
      return this.getProcessImplementations();
    },
    serviceImplementationOptions() {
      return this.serviceImplementations.map(si => {
        return { value: si.id, ...si }
      })
    },
    eventImplementationsView() {
      return this.eventImplementations
          .filter(ei => {
            return ei.processImplementationId !== this.processImplementation.id;
          })
          .map(ei => ({
            isSelected: this.value.includes(ei.id),
            isExisting: this.existingEventImplementationIds.includes(ei.id),
            ...ei
          }));
    },
    hasError() {
      return this.value.length === 0;
    },
  },
  methods: {
    ...mapActions({
      gqlQuery: 'hds/gqlQuery',
    }),
    handleSelectionChange(row, isSelected) {
      const selections = Array.from(this.value);
      const index = selections.indexOf(row.id);
      if (isSelected && index === -1) {
        selections.push(row.id);
        this.$emit('input', selections);
      } else if(!isSelected && index !== -1) {
        selections.splice(index, 1);
        this.$emit('input', selections);
      }
    },
    async loadServiceImplementations() {
      try {
        this.serviceImplementationsLoading = true;
        const {data: serviceImplementations} = await this.hcsClient.get('/service_implementations', {
          params: {
            'controller_id_eq': this.controllerId
          }
        });

        const serviceComponentModelIds = serviceImplementations
            .map(si => si.serviceComponentModelId)
            .reduce((set, value) => {
              set.add(value);
              return set;
            }, new BetterSet())
            .asArray()
            .filter(v => !!v);

        const {serviceComponentModels} = await this.gqlQuery({
          query: gql`
            query FindServiceComponentModels(
                $serviceComponentModelIds: [ID!]!,
            ) {
                serviceComponentModels: serviceComponentModelInBulk(serviceComponentModelIds: $serviceComponentModelIds) {
                    id
                    name
                    description
                }
            }`,
          variables: {
            serviceComponentModelIds
          }
        });

        this.serviceImplementations = serviceImplementations.map(si => {
          const localServiceComponentModel = serviceComponentModels.find(scm => scm.id === si.serviceComponentModelId);
          return {
            ...si,
            localServiceComponentModel
          };
        });
      } finally {
        this.serviceImplementationsLoading = false;
      }
    },
    async loadEventImplementations() {
      const selectedServiceImplementationId = this.selectedServiceImplementationId;
      if(!selectedServiceImplementationId) {
        this.eventImplementations = [];
        return;
      }

      try {
        this.eventImplementationsLoading = true;
        this.eventImplementations = [];
        const {data: eventImplementations} = await this.hcsClient.get('/event_implementations', {
          params: {
            processImplementationServiceImplementationIdEq: selectedServiceImplementationId
          }
        });
        this.eventImplementations = eventImplementations
            .filter(ei => {
              const processImplementation = this.processImplementations.find(pi => pi.id === ei.processImplementationId);
              return processImplementation?.serviceImplementationId === selectedServiceImplementationId
            })
            .map(ei => {
              const processImplementation = this.processImplementations.find(pi => pi.id === ei.processImplementationId);
              const pcm = this.hdsEntities.processComponentModel.findOne({id: processImplementation.processComponentModelId});
              return {
                id: ei.id,
                processName: pcm?.name,
                name: ei.name,
                localId: ei.localId,
                description: ei.description
              };
            });
      } finally {
        this.eventImplementationsLoading = false;
      }
    }
  },
  watch: {
    selectedServiceImplementationId: {
      immediate: true,
      handler() {
        this.loadEventImplementations();
      }
    },
    // 'dialog.visible': {
    //   handler(visible) {
    //     if(visible) {
    //       this.loadEventImplementations();
    //     }
    //   }
    // }
  }
}
</script>
